import * as React from 'react'

import ReactTable, {Filter} from 'react-table'
import {DrawSource} from '../../enums/DrawSource'
import {ensureQuery} from '../../helpers'
import {IFeatureLayerInfo, IQuery, ITableProps} from '../../interfaces/models'
import {Feature} from '../../models'
import {getMapService} from '../../reducers/map'
import {ServiceHub} from '../../ServiceHub'
import OverlayLoader from '../Loading/OverlayLoading'
import {PropsProvider} from './PropsProvider'

export interface Props extends Partial<ITableProps> {
  layerInfo: IFeatureLayerInfo
  sync: boolean
  query: IQuery
  propsProvider: PropsProvider
  onSelectedIdsChanged?: (selectedIds: {[key: string]: any}) => void
}

interface State {
  tableProps?: ITableProps
}

class FeaturesTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {}
    this.zoomAll = this.zoomAll.bind(this)
    this.setTableInfoSelections = this.setTableInfoSelections.bind(this)
    this.setTableInfoExpanded = this.setTableInfoExpanded.bind(this)
    this.toggleSelectAll = this.toggleSelectAll.bind(this)
  }
  componentDidMount() {
    this.createTableProps(this.props).then(props => {
      this.setState({...this.state, tableProps: {...props}})
    })
  }

  protected getRequestData = (tableProps: ITableProps, featureLayerInfo: IFeatureLayerInfo) => {
    return (newProps: ITableProps): Promise<{rows: any[]; pages: number}> => {
      tableProps.loading = true
      tableProps.expanded = {}
      tableProps.query = newProps.query
      tableProps.selectedIds = newProps.selectedIds
      this.setState({...this.state, tableProps})
      return new Promise((resolve, reject) => {
        tableProps.query = ensureQuery(tableProps, featureLayerInfo)
        ServiceHub.gisCrudService.executeForCount(featureLayerInfo, tableProps.query).then(
          count => {
            if (newProps.pageSize != undefined && newProps.page != undefined && newProps.sorted != undefined) {
              const query = {...tableProps.query!}
              query.num = tableProps.pageSize
              query.start = newProps.page * newProps.pageSize
              if (newProps.sorted.length > 0) {
                query.orderByFields = [`${newProps.sorted[0].id} ${newProps.sorted[0].desc ? 'DESC' : 'ASC'}`]
              }
              ServiceHub.gisCrudService.execute(featureLayerInfo, query).then(
                result => {
                  tableProps.data = result
                  tableProps.recordsCount = count
                  tableProps.pages =
                    (count - (count % tableProps.pageSize!)) / tableProps.pageSize! +
                    (count % tableProps.pageSize! > 0 ? 1 : 0)
                  tableProps.loading = false
                  delete tableProps.error
                  if (tableProps.recordsCount <= tableProps.pageSize!) {
                    tableProps.pageSize = tableProps.recordsCount
                  }
                  this.setState({...this.state, tableProps})
                },
                error => {
                  tableProps.loading = false
                  tableProps.error = error
                  this.setState({...this.state, tableProps})
                }
              )
            }
          },
          error => {
            tableProps.loading = false
            tableProps.error = error
            this.setState({...this.state, tableProps})
          }
        )
      })
    }
  }

  zoomAll() {
    const table = this.state.tableProps
    const layerInfo = this.props.layerInfo
    if (table) {
      if (table.onFetchData) {
        if (table.query && layerInfo) {
          this.setState({...this.state, tableProps: {...table, loading: true}})
          const queryLite = {...table.query, outFields: []} as IQuery
          ServiceHub.gisCrudService
            .execute(layerInfo, queryLite)
            .then(res => {
              const mapService = getMapService()
              mapService.zoomTo(res.filter(r => !!r.getGeometry()).map(r => r.getGeometry()))
              mapService.drawing.unMark(DrawSource.Select)
              mapService.drawing.mark(res, DrawSource.Select)
              this.setState({...this.state, tableProps: {...table!, loading: false}})
            })
            .catch(() => {
              this.setState({...this.state, tableProps: {...table!, loading: false}})
            })
        } else {
          throw new Error('Unfinished...')
        }
      } else {
        const mapService = getMapService()
        mapService.zoomTo((table.data as Array<Feature>).filter(r => !!r.getGeometry()).map(r => r.getGeometry()!))
        mapService.drawing.unMark(DrawSource.Select)
        mapService.drawing.mark(table.data as Array<Feature>, DrawSource.Select)
      }
    }
  }

  setTableInfoSelections(tableId: string, selectedIdsInput: any, add?: boolean): void {
    if (this.state.tableProps) {
      let selectedIds = this.state.tableProps.selectedIds || {}
      selectedIds = {...selectedIds}
      if (add === undefined) {
        selectedIds = selectedIdsInput
      } else if (add) {
        for (const key in selectedIdsInput) {
          selectedIds[key] = selectedIdsInput[key]
        }
      } else {
        for (const key in selectedIdsInput) {
          delete selectedIds[key]
        }
      }
      if (this.props.onSelectedIdsChanged) {
        this.props.onSelectedIdsChanged(selectedIds)
      }
      this.setState({...this.state, tableProps: {...this.state.tableProps, selectedIds}})
    }
  }

  setTableInfoExpanded(tableId: string, expandedInput: {[id: string]: any}, add: boolean): void {
    if (this.state.tableProps) {
      let expanded = this.state.tableProps.expanded || {}
      expanded = {...expanded}
      if (add) {
        for (const key in expandedInput) {
          ;(expanded as any)[key] = expandedInput[key]
        }
      } else {
        for (const key in expandedInput) {
          delete (expanded as any)[key]
        }
      }
      this.setState({...this.state, tableProps: {...this.state.tableProps, expanded}})
    }
  }

  toggleSelectAll(tableId: string): void {
    if (!this.state.tableProps) {
      return
    }
    let selectedIds = this.state.tableProps.selectedIds || {}
    let data = this.state.tableProps.data || []
    if (!this.state.tableProps.onFetchData) {
      if (
        this.state.tableProps.filtered &&
        this.state.tableProps.filtered.length > 0 &&
        this.state.tableProps!.defaultFilterMethod
      ) {
        this.state.tableProps.filtered.forEach(f => {
          data = data.filter(d => this.state.tableProps!.defaultFilterMethod!(f, d.getProperties(), f))
        })
      }
      if (data.length <= Object.keys(selectedIds).length) {
        this.setTableInfoSelections(tableId, {})
      } else if (data) {
        selectedIds = {...this.state.tableProps.selectedIds}
        data.forEach(d => {
          selectedIds[d.getProperties()[this.state.tableProps!.idColumnName]!] = d
        })
        this.setTableInfoSelections(tableId, selectedIds)
      }
    } else {
      if ((this.state.tableProps.recordsCount || 0) <= Object.keys(selectedIds).length) {
        this.setTableInfoSelections(tableId, {})
      }
      this.setState({...this.state, tableProps: {...this.state.tableProps, loading: true}})
      ServiceHub.gisCrudService
        .execute(this.props.layerInfo, this.state.tableProps.query!)
        .then((res: Array<Feature>) => {
          selectedIds = {}
          res.forEach(d => {
            selectedIds[d.getProperties()[this.state.tableProps!.idColumnName]!] = d
          })
          this.setTableInfoSelections(tableId, selectedIds)
          this.setState({...this.state, tableProps: {...this.state.tableProps!, loading: false}})
        })
        .catch((error: Error) => {
          this.setState({...this.state, tableProps: {...this.state.tableProps!, loading: false}})
        })
    }
  }

  createTableProps(props: Props): Promise<ITableProps> {
    return new Promise((resolve, reject) => {
      const tableProps = this.props.propsProvider.getTableProps(
        props.layerInfo,
        this.setTableInfoSelections,
        this.setTableInfoExpanded,
        this.toggleSelectAll,
        this.zoomAll,
        this.props.query
      )
      tableProps.onFilteredChange = (newFiltering: Filter[], column: any, value: any) => {
        this.setState({...this.state, tableProps: {...this.state.tableProps!, filtered: newFiltering}})
      }
      for (const key in props) {
        ;(tableProps as any)[key] = (props as any)[key]
      }
      if (props.sync) {
        tableProps.filterable = true

        ServiceHub.gisCrudService
          .execute(this.props.layerInfo, this.props.query)
          .then(r => {
            tableProps.data = r
            tableProps.recordsCount = r.length
            resolve(tableProps)
          })
          .catch(e => {
            tableProps.error = e
            resolve(tableProps)
          })
      } else {
        tableProps.manual = true
        tableProps.onFetchData = this.getRequestData(tableProps, props.layerInfo)
        resolve(tableProps)
      }
    })
  }

  render() {
    return this.state.tableProps ? (
      <ReactTable {...this.state.tableProps} />
    ) : (
      <div style={{position: 'relative', height: '100%'}}>
        <OverlayLoader />
      </div>
    )
  }
}

export default FeaturesTable
